import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Alert, type AlertTone } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useLogin } from '@/hooks/useLogin'
import type { ApiError } from '@/lib/api/errors'
import { resolvePostLoginPath } from '@/routes/postLogin'
import { useAuthStore } from '@/stores/authStore'

/**
 * 구장 관리자 로그인 — Figma `관리자 로그인`(13989-46281) 실측.
 * 헤더 56px + 중앙 카드 520px + 입력 342×40 + 로그인 버튼 88×88.
 *
 * ⚠️ 비밀번호는 **Base64** 로 인코딩해 보낸다 — `POST /venue/v1/manager/auth/login` 규약이다.
 * 인코딩 자체는 `lib/auth/password.ts` 한 곳이 담당하며 이 화면은 평문을 넘긴다(설계 R1).
 */
const schema = z.object({
  loginId: z.string().trim().min(1, '아이디를 입력해 주세요.'),
  // 비밀번호는 trim 하지 않는다 — 앞뒤 공백도 사용자가 설정한 값의 일부다.
  password: z.string().min(1, '비밀번호를 입력해 주세요.'),
})

type LoginForm = z.infer<typeof schema>

interface FailurePresentation {
  tone: AlertTone
  /** 실패 후 어디에 커서를 둘까. `null` 이면 건드리지 않는다. */
  focus: 'loginId' | 'password' | null
  /** 비밀번호 입력을 비울까. 자격 문제(구장 미연결)에서는 비우지 않는다. */
  clearPassword: boolean
  /** 서버 문구 아래 덧붙일 보조 안내. */
  hint?: string
}

const DEFAULT_PRESENTATION: FailurePresentation = {
  tone: 'danger',
  focus: 'password',
  clearPassword: true,
}

/**
 * 실패 사유별 **화면 동작**만 정의한다 (설계 §2-1(c)·D3).
 *
 * 🚨 **문구는 여기서 만들지 않는다.** 본문은 언제나 서버 `errorMsg` 이고, 이 표는 톤·포커스·
 * 보조 안내만 정한다. 서버가 문구를 고치면 배포 없이 반영된다.
 * 🚨 `reason` 은 forward-only 라 **모르는 값이 올 수 있다** — 그때는 기본 동작으로 떨어진다.
 */
const FAILURE_PRESENTATION: Record<string, FailurePresentation> = {
  ACCOUNT_NOT_FOUND: { tone: 'danger', focus: 'loginId', clearPassword: false },
  PASSWORD_MISMATCH: { tone: 'danger', focus: 'password', clearPassword: true },
  ACCOUNT_INACTIVE: {
    tone: 'danger',
    focus: null,
    clearPassword: false,
    hint: '계정 상태는 페이스콕 담당자에게 문의해 주세요.',
  },
  // 🚨 입력 오류가 아니다 — 로그인 자격은 맞는데 연결된 구장이 없는 상태다.
  // 비밀번호를 비우면 사용자가 오타로 오해해 무한 재입력한다(설계 D4).
  VENUE_NOT_LINKED: {
    tone: 'warning',
    focus: null,
    clearPassword: false,
    hint: '구장 연결은 페이스콕 담당자가 처리합니다. 구장명과 아이디를 알려 주세요.',
  },
  // 🚨 사용자 잘못이 아니라 **클라이언트 결함**이다(Base64 인코딩 회귀 — 설계 R1·R2).
  INVALID_PASSWORD_ENCODING: {
    tone: 'danger',
    focus: null,
    clearPassword: false,
    hint: '앱 오류로 로그인하지 못했습니다. 문제가 계속되면 페이스콕 담당자에게 문의해 주세요.',
  },
}

function presentationFor(error: ApiError | null): FailurePresentation {
  if (!error?.reason) return DEFAULT_PRESENTATION
  return FAILURE_PRESENTATION[error.reason] ?? DEFAULT_PRESENTATION
}

/** Figma 의 입력 좌측 아이콘(15px). 아이콘 라이브러리를 도입하지 않는다(YAGNI). */
function PersonIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-[15px] w-[15px] fill-slateish-400">
      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 1.5c-2.7 0-5 1.4-5 3.1V14h10v-1.4c0-1.7-2.3-3.1-5-3.1Z" />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-[15px] w-[15px] fill-slateish-400">
      <path d="M4.5 6.5V5a3.5 3.5 0 1 1 7 0v1.5H12A1 1 0 0 1 13 7.5v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h.5Zm1.5 0h4V5a2 2 0 1 0-4 0v1.5Z" />
    </svg>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const setLoginId = useAuthStore((state) => state.setLoginId)
  const loginMutation = useLogin()

  const {
    register,
    handleSubmit,
    resetField,
    setFocus,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { loginId: '', password: '' },
  })

  const publicSiteUrl = import.meta.env.VITE_PUBLIC_SITE_URL
  const failure = presentationFor(loginMutation.error)

  function onSubmit(values: LoginForm) {
    const loginId = values.loginId.trim()

    loginMutation.mutate(
      { loginId, password: values.password },
      {
        onSuccess: (result) => {
          // 서버가 확인해 준 계정 식별자를 우선 쓴다 — 사용자가 친 문자열보다 정확하다.
          setLoginId(result.manager?.memberId ?? loginId)
          navigate(resolvePostLoginPath(location.search), { replace: true })
        },
        onError: (error) => {
          const next = presentationFor(error)
          // 🚨 클라이언트 결함은 로그로 즉시 드러낸다 — 사용자는 고칠 수 없는 오류다.
          if (error.reason === 'INVALID_PASSWORD_ENCODING') {
            console.error('[login] 비밀번호 인코딩이 서버 규약과 어긋납니다(Base64).', error.message)
          }
          if (next.clearPassword) resetField('password')
          if (next.focus) setFocus(next.focus)
        },
      }
    )
  }

  return (
    <div className="flex min-h-full flex-col bg-slateish-50">
      <header className="flex h-14 items-center justify-between border-b border-slateish-200 bg-white px-6">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-500 text-xs font-bold text-white">
            FC
          </span>
          <span className="text-base font-semibold text-ink-700">페이스콕 구장 관리</span>
        </div>
        {/* 대상 URL 이 확정되지 않았으므로 미설정 시 링크 자체를 렌더하지 않는다(설계 D6). */}
        {publicSiteUrl ? (
          <a
            href={publicSiteUrl}
            className="text-sm text-brand-500"
            rel="noreferrer noopener"
            target="_blank"
          >
            페이스콕 홈페이지로 돌아가기
          </a>
        ) : null}
      </header>

      <div className="flex flex-1 justify-center px-6 py-[72px]">
        <div className="w-full max-w-[520px]">
          <div className="rounded-xl border border-slateish-200 bg-white px-10 py-8 shadow-sm">
            <h1 className="text-center text-lg font-semibold text-ink-700">구장 관리자 로그인</h1>
            <p className="mt-2 text-center text-[13px] text-slateish-600">
              구장 관리자 또는 운영 담당자만 로그인할 수 있습니다.
            </p>

            {loginMutation.isError ? (
              <Alert tone={failure.tone} className="mt-5">
                {/* 서버가 준 문구를 그대로 보여준다 — 실패 사유는 서버가 가장 정확히 안다(D3). */}
                <p>{loginMutation.error.message}</p>
                {failure.hint ? <p className="mt-1">{failure.hint}</p> : null}
              </Alert>
            ) : null}

            <form
              className="mt-6 flex flex-col gap-2 sm:flex-row"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex flex-1 flex-col gap-2">
                <div>
                  <label htmlFor="login-id" className="sr-only">
                    아이디
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                      <PersonIcon />
                    </span>
                    <Input
                      id="login-id"
                      className="h-10 pl-9"
                      placeholder="아이디"
                      autoComplete="username"
                      aria-invalid={errors.loginId ? true : undefined}
                      {...register('loginId')}
                    />
                  </div>
                  {errors.loginId ? (
                    <p className="mt-1 text-xs text-danger-500">{errors.loginId.message}</p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="login-password" className="sr-only">
                    비밀번호
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                      <LockIcon />
                    </span>
                    <Input
                      id="login-password"
                      type="password"
                      className="h-10 pl-9"
                      placeholder="비밀번호"
                      autoComplete="current-password"
                      aria-invalid={errors.password ? true : undefined}
                      {...register('password')}
                    />
                  </div>
                  {errors.password ? (
                    <p className="mt-1 text-xs text-danger-500">{errors.password.message}</p>
                  ) : null}
                </div>
              </div>

              <Button
                type="submit"
                className="h-11 sm:h-[88px] sm:w-[88px]"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? '로그인 중' : '로그인'}
              </Button>
            </form>
          </div>

          {/*
            Figma 하단 "자주 묻는 질문" 블록. 🚨 **라우트만 연결**한다 — 비밀번호 재설정·가입
            기능은 서버가 소유하지 않아 안내 화면으로 보낸다(설계 §2-1(g)·D10).
          */}
          <div className="mt-10 space-y-1 text-center text-[13px] text-slateish-400">
            <p className="font-medium">자주 묻는 질문</p>
            <p>
              <Link to="/account/password-reset" className="text-slateish-600 underline">
                비밀번호를 잊으셨나요?
              </Link>
            </p>
            <p>
              <Link to="/account/signup-guide" className="text-slateish-600 underline">
                구장 입점이 처음이신가요? 구장 관리자 가입 안내 ›
              </Link>
            </p>
            <p>
              <Link to="/account/signup-guide" className="text-slateish-600 underline">
                어떤 계정이 관리자 계정인지 알 수 없나요 ›
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
